import moment from "moment";
import { formatCurrency, formatSubscriptionStatusLabel } from "./billingOverview.utils";
import { buildTenantsListPath, DEFAULT_TENANT_FILTERS } from "../Tenants/tenantsList.utils";

export const STRIPE_CUSTOMER_LINK_FILTERS = [
  { id: "all", label: "All Stripe tenants" },
  { id: "active", label: "Active customer ID" },
  { id: "historical", label: "Historical only" },
];

export function formatStripeMetricValue(value) {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  return value;
}

export function formatPlanCatalogAmount(row = {}) {
  if (row.unit_amount == null) {
    return "—";
  }

  return `${(row.unit_amount / 100).toFixed(2)} ${row.currency || ""}`.trim();
}

export function buildStripeOverviewMetrics(
  plans = [],
  billing = null,
  customerSummary = null,
  stripeDashboard = null
) {
  const stats = billing?.stats || {};
  const revenue = billing?.revenue_summary || {};
  const productCount = new Set(plans.map((row) => row.product_id).filter(Boolean)).size;
  const dashboard =
    stripeDashboard ||
    billing?.revenue_summary?.stripe_dashboard ||
    billing?.stripe_dashboard ||
    null;

  return [
    {
      id: "stripe_customers",
      label: "Stripe tenants",
      value: formatStripeMetricValue(customerSummary?.total),
      hint: "Current or historical Stripe customers",
      externalUrl: dashboard?.customers_url,
    },
    {
      id: "products",
      label: "Active products",
      value: formatStripeMetricValue(productCount),
      externalUrl: dashboard?.products_url,
    },
    {
      id: "prices",
      label: "Active prices",
      value: formatStripeMetricValue(plans.length),
      externalUrl: dashboard?.products_url,
    },
    {
      id: "paying_tenants",
      label: "Paying tenants",
      value: formatStripeMetricValue(customerSummary?.paying ?? stats.active_subscriptions),
      to: buildTenantsListPath({ ...DEFAULT_TENANT_FILTERS }),
    },
    {
      id: "last_sync",
      label: "Last invoice sync",
      value: revenue.last_stripe_sync_at
        ? moment(revenue.last_stripe_sync_at).fromNow()
        : "Never",
      externalUrl: dashboard?.invoices_url,
    },
  ];
}

export function formatStripeCustomerStatusLabel(status) {
  return formatSubscriptionStatusLabel(status);
}

export function getStripeCustomerStatusVariant(row = {}) {
  const status = row.subscription_status;

  if (row.paying) {
    return "success";
  }

  if (row.on_trial) {
    return "warning";
  }

  switch (status) {
    case "active":
      return "success";
    case "past_due":
    case "unpaid":
    case "incomplete_expired":
      return "danger";
    case "canceled":
      return "neutral";
    default:
      return "neutral";
  }
}

export function getStripeLinkStatusVariant(linkStatus) {
  return linkStatus === "active" ? "success" : "warning";
}

export function formatStripeTimestamp(value) {
  if (!value) {
    return "—";
  }

  return moment(value).format("D MMM YYYY");
}

export function buildPlanAssignmentRows(planBreakdown = []) {
  return (planBreakdown || []).map((row, index) => ({
    id: row.plan_name || index,
    plan_name: row.plan_name || "Unassigned",
    tenant_count: row.tenant_count ?? 0,
    paying_count: row.paying_count ?? 0,
    trial_count: row.trial_count ?? 0,
    failed_count: row.failed_count ?? 0,
    estimated_revenue: row.estimated_monthly_revenue_cents
      ? formatCurrency(row.estimated_monthly_revenue_cents, row.currency)
      : "—",
  }));
}

export function getStripeManagementSubtitle({
  plansCount = 0,
  billing = null,
  customerSummary = null,
} = {}) {
  const parts = [];

  if (customerSummary?.total != null) {
    parts.push(`${customerSummary.total} Stripe tenant(s)`);
  }

  parts.push(`${plansCount} active Stripe price(s)`);

  if (billing?.revenue_summary?.last_stripe_sync_at) {
    parts.push(
      `invoices synced ${moment(billing.revenue_summary.last_stripe_sync_at).fromNow()}`
    );
  }

  return parts.join(" · ");
}

export function getStripeManagementTabs({ canManagePlans, canViewBilling }) {
  const tabs = [];

  if (canViewBilling) {
    tabs.push({ id: "customers", label: "Stripe customers" });
  }

  if (canManagePlans) {
    tabs.push({ id: "plans", label: "Plans & pricing" });
  }

  if (canViewBilling) {
    tabs.push(
      { id: "assignments", label: "Tenant assignments" },
      { id: "subscriptions", label: "Subscriptions" },
      { id: "sync", label: "Invoice sync" }
    );
  }

  return tabs;
}
